import {
  Button,
  Column,
  Copy,
  Flex,
  Loader,
  Row,
  Section,
  Square,
  Squish,
  Stack,
  SvgReceipt,
} from '@grubhubprod/gh-cookbook-react/dist';
import React from 'react';
import classnames from 'classnames';
import { useParams } from 'react-router-dom';
import { NoResultsMsg } from 'src/components/Table/NoResultsMsg';
import {
  SortableColumn,
  SortFields,
  SortOrder,
} from 'src/components/Table/SortableColumn';
import Table from 'src/components/Table/Table';
import { IOrdersHistory } from 'src/types/OrderHistory';
import {
  currencyFormatter,
  transformPenniesToDollar,
} from 'src/utilities/currency-utils';
import { useScreenSizes } from 'src/utilities/useScreenSizes';
import { getFormattedTimezoneFromObject } from 'src/utilities/time-utils';
import { RouterLink } from 'src/components/RouterLink/RouterLink';
import { RouterButtonLink } from 'src/components/RouterLink/RouterButtonLink';
import { MEAL_CREDIT_ENTITY_NAME } from 'src/constants/mealCredit';

export interface IOrderHistoryTableProps {
  // TODO (mbeade) combine search information into one prop
  orderList: IOrdersHistory[];
  onSortColumnClick: (field: string, order: string) => void;
  onLoadMore: (isRetry: boolean) => void;
  totalItems: number;
  pageSize?: number;
  previousSortField: SortFields;
  previousSortOrder: SortOrder;
  isLoadingMoreResults: boolean;
  showLoadMore: boolean;
  showError: boolean;
  initialSearchCompletedWithNoResults: boolean;
  isLoading: boolean;
}

const DATE_FORMAT = 'MMM d, yyyy h:mma';
const FIXED_DESKTOP_BODY_HEIGHT = '532px';

export default function OrderHistoryTable({
  orderList,
  onSortColumnClick,
  pageSize,
  totalItems,
  onLoadMore,
  isLoadingMoreResults,
  showLoadMore,
  showError,
  previousSortField,
  previousSortOrder,
  initialSearchCompletedWithNoResults,
  isLoading,
}: IOrderHistoryTableProps): JSX.Element {
  const { isTablet, isDesktop } = useScreenSizes();
  const { corpid } = useParams<{ corpid: string }>();

  const remainingResults = totalItems - orderList.length;
  const pageLeftCount =
    remainingResults > pageSize ? pageSize : remainingResults;

  return (
    <Table
      showFooter={false}
      hideHeader={!isDesktop}
      bodyMinHeight={FIXED_DESKTOP_BODY_HEIGHT}
      bodyMaxHeight={isDesktop ? FIXED_DESKTOP_BODY_HEIGHT : 'auto'}
      isLoading={isLoading}
      header={() => (
        <>
          {isDesktop && (
            <Row
              gutter={0}
              style={{ marginRight: orderList.length > 10 ? '16px' : '0' }}
            >
              <Column tablet={3} gutter={0}>
                <SortableColumn
                  label="Delivery date"
                  previousSortOrder={previousSortOrder}
                  sortField={SortFields.WHEN_FOR}
                  previousSortField={previousSortField}
                  onSortColumnClick={onSortColumnClick}
                />
              </Column>
              <Column tablet={3} gutter={0}>
                <SortableColumn
                  label="Name"
                  previousSortOrder={previousSortOrder}
                  sortField={SortFields.DINER_NAME}
                  previousSortField={previousSortField}
                  onSortColumnClick={onSortColumnClick}
                />
              </Column>
              <Column tablet={3} gutter={0}>
                <SortableColumn
                  label="Restaurant"
                  previousSortOrder={previousSortOrder}
                  sortField={SortFields.RESTAURANT}
                  previousSortField={previousSortField}
                  onSortColumnClick={onSortColumnClick}
                />
              </Column>
              <Column tablet={1} gutter={0}>
                <Flex justifyContent="flex-end">
                  <Copy variant="body" color="secondaryText">
                    Total
                  </Copy>
                </Flex>
              </Column>
              <Column tablet={2} gutter={0} />
            </Row>
          )}
        </>
      )}
      body={() => (
        <>
          {orderList.length > 0 &&
            orderList.map((order: IOrdersHistory) => (
              <div key={order.order_id}>
                {isDesktop && (
                  <>
                    <Square
                      inset="md"
                      borderBottom="1px solid"
                      borderBottomColor="lightLine"
                    >
                      <Row gutter={0}>
                        <Column tablet={3} gutter={0}>
                          {getFormattedTimezoneFromObject(
                            order.delivery_on_utc,
                            order.time_zone,
                            DATE_FORMAT
                          )}
                        </Column>
                        <Column tablet={3} gutter={0}>
                          {order.ordered_by.name}
                        </Column>
                        <Column tablet={3} gutter={0}>
                          {order.restaurant_name}
                        </Column>
                        <Column tablet={1} gutter={0}>
                          <Flex justifyContent="flex-end">
                            {currencyFormatter(
                              Number(
                                transformPenniesToDollar(order.total_expense)
                              )
                            )}
                          </Flex>
                        </Column>
                        <Column tablet={2} gutter={0}>
                          <Flex alignItems="center" justifyContent="flex-end">
                            <RouterButtonLink
                              href={`/corporate/management/${corpid}/orderHistory/${order.order_id}`}
                              buttonVariant="tertiary"
                              buttonSize="medium"
                              className={classnames(
                                'removeButtonPadding',
                                'autoButtonHeight'
                              )}
                            >
                              View receipt
                            </RouterButtonLink>
                          </Flex>
                        </Column>
                      </Row>
                    </Square>
                  </>
                )}

                {!isDesktop && (
                  <Section inset="sm" className="orderHistoryTable_mobileRow">
                    <Flex alignItems="center" justifyContent="space-between">
                      <Flex flexDirection="column">
                        <Stack y="xxx-sm">
                          <Copy variant="body" color="primaryText">
                            {order.ordered_by.name}
                          </Copy>
                        </Stack>
                        <Stack y="xxx-sm">
                          <Copy variant="body" color="primaryText">
                            {order.restaurant_name}
                          </Copy>
                        </Stack>
                        <Stack y="xxx-sm">
                          <Copy variant="body" color="secondaryText">
                            {getFormattedTimezoneFromObject(
                              order.delivery_on_utc,
                              order.time_zone,
                              DATE_FORMAT
                            )}
                          </Copy>
                        </Stack>
                      </Flex>

                      <Flex
                        alignItems={!isTablet ? 'flex-end' : 'center'}
                        justifyContent="space-between"
                        flexDirection={isTablet ? 'row' : 'column'}
                      >
                        <Stack
                          x={isTablet ? 'x-sm' : 'none'}
                          y={!isTablet ? 'xxx-sm' : 'none'}
                        >
                          {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                          <Squish {...(isTablet && { inset: 'sm' })}>
                            <Flex flexDirection="column" alignItems="flex-end">
                              <Stack y="xxx-sm">
                                <Copy variant="body" color="secondaryText">
                                  Total
                                </Copy>
                              </Stack>
                              <Copy variant="body" color="primaryText">
                                {currencyFormatter(
                                  Number(
                                    transformPenniesToDollar(
                                      order.total_expense
                                    )
                                  )
                                )}
                              </Copy>
                            </Flex>
                          </Squish>
                        </Stack>

                        <Copy variant="body" color="primaryText">
                          <RouterLink
                            href={`/corporate/management/${corpid}/orderHistory/${order.order_id}`}
                          >
                            <SvgReceipt
                              aria-label="View receipt"
                              size={isTablet ? '32px' : '24px'}
                            />
                          </RouterLink>
                        </Copy>
                      </Flex>
                    </Flex>
                  </Section>
                )}
              </div>
            ))}

          {renderLoadMoreRow()}

          {orderList.length === 0 && !initialSearchCompletedWithNoResults && (
            <NoResultsMsg message="We couldn’t find what you were looking for. Give it another go." />
          )}

          {orderList.length === 0 && initialSearchCompletedWithNoResults && (
            <NoResultsMsg
              message={`Come back after people start ordering with their ${MEAL_CREDIT_ENTITY_NAME.singular}`}
              secondaryMessage="Make sure to add people to the credit so they can order."
            />
          )}
        </>
      )}
    />
  );

  function renderLoadMoreRow() {
    return (
      <>
        {!isLoadingMoreResults && showLoadMore && !showError && (
          <Flex alignItems="center" justifyContent="center" minHeight="72px">
            <Square inset="sm" width="100%">
              <Button
                data-testid="load-more-button"
                variant="secondary"
                type="button"
                buttonSize="large"
                block
                onClick={() => onLoadMore(false)}
              >
                {`Load ${pageLeftCount} more`}
              </Button>
            </Square>
          </Flex>
        )}
        {!isLoadingMoreResults && showError && (
          <Square inset="sm">
            <Flex
              alignItems="center"
              justifyContent="center"
              flexDirection={isDesktop ? 'row' : 'column'}
              minHeight="72px"
            >
              <Copy variant="body" color="primaryText">
                Something went wrong loading your results.
              </Copy>
              <Button
                data-testid="retry-button"
                variant="tertiary"
                type="button"
                buttonSize="medium"
                onClick={() => onLoadMore(true)}
              >
                Try again
              </Button>
            </Flex>
          </Square>
        )}
        {isLoadingMoreResults && (
          <Flex alignItems="center" justifyContent="center" minHeight="72px">
            <Square inset="sm">
              <Loader size="md" />
            </Square>
          </Flex>
        )}
      </>
    );
  }
}
